import { Button, Grid, TextField, Box, Typography, Paper, Fade } from "@mui/material";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { sendOtp, verifyOtpLogin } from "../State/Auth/Action";
import { toast } from "react-toastify";
import { Email, Phone, Lock, Close } from "@mui/icons-material";

const Register = ({ handleClose, switchToLogin }) => {
  const dispatch = useDispatch();
  const { auth } = useSelector((store) => store);

  const [formData, setFormData] = useState({
    email: "",
    phone: "",
    otp: "",
  });

  const [otpSent, setOtpSent] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (auth.user?.role === "CUSTOMER") {
      window.location.reload();
      handleClose?.();
    }
  }, [auth.user, handleClose]);

  useEffect(() => {
    if (auth?.otp?.status === 200) {
      setOtpSent(true);
    }
    if (!formData.email.trim()) {
      setOtpSent(false);
    }
  }, [auth?.otp, formData.email]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if ((name === "phone" || name === "otp") && !/^\d*$/.test(value)) return;

    if (name === "email" && value && !/^\S+@\S+\.\S+$/.test(value)) {
      setError("Invalid email");
    } else {
      setError("");
    }
    setFormData({ ...formData, [name]: value });
  };

  const handleSendOtp = () => {
    const identifier = formData.email || formData.phone;
    if (!identifier || (formData.phone && formData.phone.length !== 10)) {
      toast.error("Enter a valid email or 10-digit phone number");
      return;
    }

    dispatch(sendOtp(identifier));

  };

  const handleVerifyOtp = () => {
    const identifier = formData.email || formData.phone;
    const otpValue = formData.otp;
    if (!otpValue || otpValue.length !== 4) {
      toast.error("Enter a valid 4-digit OTP");
      return;
    }

    dispatch(verifyOtpLogin(identifier, otpValue));

  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!otpSent) {
      handleSendOtp();
    } else {
      handleVerifyOtp();
    }
  };

  return (
    <Fade in timeout={600}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          minHeight: '20vh',
          bgcolor: '#fff',
          pt: 3,
          pb: 3,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: { xs: '95%', sm: '400px' },
            bgcolor: 'white',
            borderRadius: '20px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
            overflow: 'hidden',
            border: '1px solid #f1f5f9',
            background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
            position: 'relative'
          }}
        >
          {/* Close Button */}
          <Box sx={{ position: 'absolute', top: 12, right: 12, zIndex: 10 }}>
            <Button
              onClick={handleClose}
              sx={{
                minWidth: 'auto',
                width: 32,
                height: 32,
                borderRadius: '50%',
                bgcolor: '#f8fafc',
                color: '#6b7280',
                border: '1px solid #e2e8f0',
                '&:hover': {
                  bgcolor: '#f1f5f9',
                  color: '#374151',
                  transform: 'scale(1.05)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              <Close sx={{ fontSize: '1rem' }} />
            </Button>
          </Box>

          {/* Header Section */}
          <Box sx={{
            width: '100%',
            p: 4,
            textAlign: 'center',
            bgcolor: '#f8fafc',
            borderBottom: '1px solid #e2e8f0',
            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Decorative Elements */}
            <Box sx={{
              position: 'absolute',
              top: -20,
              right: -20,
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: 'rgba(55, 65, 81, 0.05)',
              zIndex: 1
            }} />
            <Box sx={{
              position: 'absolute',
              bottom: -30,
              left: -30,
              width: 60,
              height: 60,
              borderRadius: '50%',
              bgcolor: 'rgba(55, 65, 81, 0.03)',
              zIndex: 1
            }} />

            <Typography
              variant="h5"
              sx={{
                fontWeight: '800',
                color: '#374151',
                textTransform: 'uppercase',
                fontSize: '1.375rem',
                letterSpacing: '1.5px',
                mb: 1,
                position: 'relative',
                zIndex: 2
              }}
            >
              Inarifashion
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#6b7280',
                fontSize: '0.875rem',
                fontWeight: '500',
                position: 'relative',
                zIndex: 2
              }}
            >
              Premium Fashion & Lifestyle
            </Typography>
          </Box>

          {/* Registration Form Section */}
          <Box sx={{ p: 4 }}>
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                fontWeight: '700',
                textAlign: 'center',
                color: '#1f2937',
                fontSize: '1.25rem'
              }}
            >
              Create Account
            </Typography>

            <Typography
              variant="body2"
              sx={{
                mb: 4,
                textAlign: 'center',
                color: '#6b7280',
                fontSize: '0.875rem',
                lineHeight: 1.5
              }}
            >
              Enter your details to create your account
            </Typography>

            <form onSubmit={handleSubmit}>
              <Grid container spacing={3} direction="column">
                {/* Email Input */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="email"
                    name="email"
                    label="Email Address"
                    type="email"
                    variant="outlined"
                    value={formData.email}
                    onChange={handleChange}
                    error={!!error}
                    helperText={error}
                    placeholder="Enter your email"
                    InputProps={{
                      startAdornment: <Email sx={{ mr: 1.5, color: '#9ca3af', fontSize: '1.25rem' }} />,
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        fontSize: '0.875rem',
                        bgcolor: '#fafafa',
                        '&:hover': {
                          bgcolor: '#f8fafc',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#d1d5db',
                            borderWidth: '2px',
                          },
                        },
                        '&.Mui-focused': {
                          bgcolor: '#ffffff',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#374151',
                            borderWidth: '2px',
                          },
                        },
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '0.875rem',
                        color: '#6b7280',
                        fontWeight: '500',
                      },
                      '& .MuiFormHelperText-root': {
                        fontSize: '0.75rem',
                        color: '#ef4444',
                      },
                    }}
                  />
                </Grid>

                {/* OR Divider */}
                <Grid item xs={12} sx={{ textAlign: 'center', my: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#9ca3af',
                      fontWeight: '700',
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '1px'
                    }}
                  >
                    OR
                  </Typography>
                </Grid>

                {/* Phone Input */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="phoneNumber"
                    name="phone"
                    label="Phone Number"
                    type="tel"
                    variant="outlined"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Enter your phone number"
                    inputProps={{ maxLength: 10 }}
                    InputProps={{
                      startAdornment: <Phone sx={{ mr: 1.5, color: '#9ca3af', fontSize: '1.25rem' }} />,
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        fontSize: '0.875rem',
                        bgcolor: '#fafafa',
                        '&:hover': {
                          bgcolor: '#f8fafc',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#d1d5db',
                            borderWidth: '2px',
                          },
                        },
                        '&.Mui-focused': {
                          bgcolor: '#ffffff',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#374151',
                            borderWidth: '2px',
                          },
                        },
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '0.875rem',
                        color: '#6b7280',
                        fontWeight: '500',
                      },
                    }}
                  />
                </Grid>

                {/* OTP Input */}
                {otpSent && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      id="otp"
                      name="otp"
                      label="OTP"
                      type="text"
                      variant="outlined"
                      value={formData.otp}
                      onChange={handleChange}
                      placeholder="Enter 4-digit OTP"
                      inputProps={{ maxLength: 4 }}
                      InputProps={{
                        startAdornment: <Lock sx={{ mr: 1.5, color: '#9ca3af', fontSize: '1.25rem' }} />,
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                          fontSize: '0.875rem',
                          bgcolor: '#fafafa',
                          '&:hover': {
                            bgcolor: '#f8fafc',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#d1d5db',
                              borderWidth: '2px',
                            },
                          },
                          '&.Mui-focused': {
                            bgcolor: '#ffffff',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#374151',
                              borderWidth: '2px',
                            },
                          },
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '0.875rem',
                          color: '#6b7280',
                          fontWeight: '500',
                        },
                      }}
                    />
                  </Grid>
                )}

                {/* Submit Button */}
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{
                      py: 2,
                      bgcolor: '#374151',
                      borderRadius: '12px',
                      '&:hover': {
                        bgcolor: '#1f2937',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 25px rgba(55, 65, 81, 0.4)',
                      },
                      color: '#fff',
                      fontWeight: '700',
                      textTransform: 'uppercase',
                      fontSize: '0.875rem',
                      letterSpacing: '1px',
                      transition: 'all 0.3s ease-in-out',
                      boxShadow: '0 4px 15px rgba(55, 65, 81, 0.3)',
                    }}
                  >
                    {otpSent ? "Verify OTP" : "Send OTP"}
                  </Button>
                </Grid>
              </Grid>
            </form>

            {/* Login Link */}
            <Typography
              variant="body2"
              sx={{
                mt: 3,
                textAlign: 'center',
                fontSize: '0.875rem',
                color: '#6b7280'
              }}
            >
              Already have an account?{' '}
              <span
                style={{
                  color: '#374151',
                  textDecoration: 'none',
                  cursor: "pointer",
                  fontWeight: '700',
                  borderBottom: '1px solid #d1d5db'
                }}
                onClick={switchToLogin}
              >
                Login
              </span>
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Fade>
  );
};

export default Register;
